"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import ICON_VS_HOTMATCH from "@/assets/images/vs_hotmatch.svg";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import LuMatchTime from "@/components/LuMatchTime";
import LuOddsItem from "@/components/LuOddsItem";
import LuOtherBetsDropdown from "@/components/LuOtherBetsDropdown/LuOtherBetsDropdown";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import { LIVE_STATUSES, FINISHED_STATUSES, NOT_STARTED_STATUS } from "@/contants";

export default function LuMatch({ match }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);

  const { getMatchOdds } = useMatches();
  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    isHomeWin,
    isAwayWin,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const odds = useMatchOdds(match, filterOdds, companyIdOdds);
  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = odds;

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isFinished = FINISHED_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const asText = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  };

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds) : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const correctScoresHalf = splitCorrectScoresListToSections(correctScoresListHalf);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds) ? otherOddsData.totalCornersHalfOdds : [];
  const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
  const hasOtherData = goalOddEvenOdds?.length > 0 || correctScoresListFull.length > 0 || totalCornersOdds.length > 0 || correctScoresListHalf.length > 0 || totalCornersHalfOdds.length > 0;

  return (
    <div className={`flex cursor-pointer flex-col overflow-hidden border-b border-lu-match-border kbv-gs-match-border first:mt-0 last:border-b-0 bg-[var(--theme-foreground)] md:hover:border-[var(--theme-border)] kbv-gs-match-row box-border ${showOther ? "active" : ""}`}>
      {/* Main Match Row Content */}
      <div className="flex flex-col relative w-full py-3 md:px-3 box-border">
        {/* Mobile teams name */}
        <div className="my-1 flex w-full min-w-0 flex-row items-center justify-center gap-x-2 overflow-hidden px-3 first:mt-3 md:hidden box-border">
          <div className="flex min-w-0 flex-1 items-center justify-end gap-x-1 overflow-hidden box-border">
            <span className="truncate text-[14px] font-semibold leading-tight text-[var(--theme-text)]">
              {match?.homeName || "-"}
            </span>
            {homeLogo ? (
              <img
                alt={match?.homeName || "home"}
                loading="lazy"
                width={20}
                height={20}
                className="block h-5 w-5 shrink-0 object-contain"
                src={homeLogo}
              />
            ) : null}
          </div>
          <div className="flex shrink-0 items-center justify-center box-border">
            <img
              alt="vs"
              loading="lazy"
              width={20}
              height={12}
              className="block h-[12px] w-5 shrink-0 object-contain"
              src={ICON_VS_HOTMATCH}
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-start gap-x-1 overflow-hidden box-border">
            {awayLogo ? (
              <img
                alt={match?.awayName || "away"}
                loading="lazy"
                width={20}
                height={20}
                className="block h-5 w-5 shrink-0 object-contain"
                src={awayLogo}
              />
            ) : null}
            <span className="truncate text-[14px] font-semibold leading-tight text-[var(--theme-text)]">
              {match?.awayName || "-"}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-center justify-center md:hidden box-border">
          <LuMatchTime
            isLive={isLive}
            isFinished={isFinished}
            isNotStarted={isNotStarted}
            matchTime={match?.matchTime}
            liveScore={liveScore}
            status={statusNum}
            halfStartTime={match?.halfStartTime}
            inline
            currentHomeScore={currentHomeScore}
            currentAwayScore={currentAwayScore}
          />
        </div>

        <div className="grid min-w-0 flex-1 grid-cols-3 gap-y-2 mt-1 gap-x-0.5 grid-rows-[auto_auto] px-1.5 pb-2 md:grid-cols-[minmax(220px,1fr)_152px_152px_160px] md:grid-rows-[auto_auto] md:gap-x-2 md:gap-y-1 md:px-0 md:pb-3 md:items-center md:py-0 box-border">
          <div className="max-md:hidden col-span-1 md:row-span-2 flex min-w-0 items-center gap-2 box-border">
            {/* Time */}
            <div className="shrink-0 box-border">
              <LuMatchTime
                homeCorner={currentHomeCorner}
                awayCorner={currentAwayCorner}
                isLive={isLive}
                isFinished={isFinished}
                isNotStarted={isNotStarted}
                matchTime={match?.matchTime}
                liveScore={liveScore}
                status={statusNum}
                halfStartTime={match?.halfStartTime}
                textAlignment="left"
                cornerStacked
                isHomeWin={isHomeWin}
                isAwayWin={isAwayWin}
                currentHomeScore={currentHomeScore}
                currentAwayScore={currentAwayScore}
                className="[&_span]:!font-bold"
              />
            </div>
            {/* Teams */}
            <div className="flex min-w-0 flex-1 items-center justify-start gap-2 md:ml-2 md:gap-2 box-border">
              <div className="flex min-w-0 max-w-[45%] flex-1 items-center justify-end gap-2 md:min-w-0 md:max-w-[200px] md:flex-1 box-border">
                <span className="truncate text-right text-[14px] font-semibold leading-tight text-[var(--theme-text)]">
                  {match?.homeName || "-"}
                </span>
                {homeLogo ? (
                  <img
                    alt={match?.homeName || "home"}
                    loading="lazy"
                    width={24}
                    height={24}
                    className="block h-6 w-6 shrink-0 object-contain"
                    src={homeLogo}
                  />
                ) : null}
              </div>

              <div className="flex shrink-0 items-center justify-center box-border">
                <img
                  alt="vs"
                  loading="lazy"
                  width={24}
                  height={14}
                  className="block h-[14px] w-6 shrink-0 object-contain"
                  src={ICON_VS_HOTMATCH}
                />
              </div>

              <div className="flex min-w-0 max-w-[45%] flex-1 items-center justify-start gap-2 md:min-w-0 md:max-w-[200px] md:flex-1 box-border">
                {awayLogo ? (
                  <img
                    alt={match?.awayName || "away"}
                    loading="lazy"
                    width={24}
                    height={24}
                    className="block h-6 w-6 shrink-0 object-contain"
                    src={awayLogo}
                  />
                ) : null}
                <span className="truncate text-left text-[14px] font-semibold leading-tight text-[var(--theme-text)]">
                  {match?.awayName || "-"}
                </span>
              </div>
            </div>
          </div>
          <LuOddsItem
            title="Kèo chấp"
            left={asText(homeHandicapOdds)}
            middle={asText(handicapValue)}
            right={asText(awayHandicapOdds)}
            leftTrend={homeHandicapTrend}
            rightTrend={awayHandicapTrend}
            tagText="Toàn trận"
            tagColorClass="kbv-tag-ft"
          />
          <LuOddsItem
            title="Tài/Xỉu"
            left={asText(overOdds)}
            middle={asText(overUnderValue)}
            right={asText(underOdds)}
            leftTrend={overTrend}
            rightTrend={underTrend}
            tagText="Toàn trận"
            tagColorClass="kbv-tag-ft"
          />
          <LuOddsItem
            title="1X2"
            left={asText(homeEuropeOdds)}
            middle={asText(instantDrawOdds)}
            right={asText(awayEuropeOdds)}
            leftTrend={homeEuropeTrend}
            middleTrend={drawTrend}
            rightTrend={awayEuropeTrend}
            tagText="Toàn trận"
            tagColorClass="kbv-tag-ft"
          />

          {/* ROW 2: Hiệp 1 (HT) - Direct siblings */}
          <LuOddsItem
            title="Kèo chấp (H1)"
            left={asText(homeHandicapHalfOdds)}
            middle={asText(handicapHalfValue)}
            right={asText(awayHandicapHalfOdds)}
            leftTrend={homeHandicapHalfTrend}
            rightTrend={awayHandicapHalfTrend}
            tagText="Hiệp 1"
            tagColorClass="kbv-tag-ht"
          />
          <LuOddsItem
            title="Tài/Xỉu (H1)"
            left={asText(overHalfOdds)}
            middle={asText(overUnderHalfValue)}
            right={asText(underHalfOdds)}
            leftTrend={overHalfTrend}
            rightTrend={underHalfTrend}
            tagText="Hiệp 1"
            tagColorClass="kbv-tag-ht"
          />
          <LuOddsItem
            title="1X2 (H1)"
            left={asText(homeEuropeHalfOdds)}
            middle={asText(drawHalfOdds)}
            right={asText(awayEuropeHalfOdds)}
            leftTrend={homeEuropeHalfTrend}
            middleTrend={drawHalfTrend}
            rightTrend={awayEuropeHalfTrend}
            tagText="Hiệp 1"
            tagColorClass="kbv-tag-ht"
          />
        </div>

      {match?.countOtherOdds  >1  && (
        <div 
          onClick={handleOtherOddsClick}
          className="max-md:hidden absolute bottom-1 right-3 cursor-pointer flex items-center gap-x-1 hover:opacity-80 group box-border"
        >
          <span className="text-[11px] font-medium text-[var(--theme-text)] opacity-60 group-hover:opacity-100 transition-opacity">
            {showOther ? "Thu gọn" : `${match?.countOtherOdds || ""} kèo khác`}
          </span>
          <img src={ICON_ARROW_UP} alt="" className={`block w-2.5 h-2.5 opacity-60 group-hover:opacity-100 transition-all ${showOther ? "rotate-180" : ""}`} />
        </div>
      )}
      {match?.countOtherOdds  >1  && (
        <div className="md:hidden flex flex-row items-center justify-end pr-3 pt-0.5 pb-2 box-border">
          <button
            onClick={handleOtherOddsClick}
            className="flex flex-row items-center justify-center gap-x-1 px-2 py-1 border-none bg-transparent outline-none cursor-pointer"
          >
            <span className="text-[12px] font-medium text-[var(--theme-text)]">
              {match?.countOtherOdds || 0} kèo
            </span>
            <img src={ICON_ARROW_UP} alt="" className={`block size-3 transition-transform ${showOther ? "rotate-180" : ""}`} />
          </button>
        </div>
        )}
      </div>

      {showOther && (
        <LuOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          isLive={isLive}
          goalOddEvenOdds={goalOddEvenOdds}
          goalOddEvenFirst={goalOddEvenFirst}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          correctScoresListHalf={correctScoresListHalf}
          correctScoresHalf={correctScoresHalf}
          totalCornersOdds={totalCornersOdds}
          totalCornersHalfOdds={totalCornersHalfOdds}
          odds={odds}
          showOther={showOther}
          fontBody="text-[13px]"
        />
      )}
    </div>
  );
}
